import { useState } from "react";
import { Button, WithActions, Pencil } from "components";
import { Modal } from "features/Modal";
import { ParkingEditSeparateModal } from "../ParkingEditSeparateModal";
import { useUpdateParkingMutation } from "services";
import { Language } from "helpers";
import { t } from "i18next";

export type ModalStep = "initial" | "success" | "error";

export interface FormData {
  parking_name_mk: string;
  parking_name_al: string;
  parking_name_en: string;
  longitude: string;
  latitude: string;
  municipality_id: number;
  area_type: string;
  total_parking_slots: number;
  payment_options: number[];
  amenities: number[];
  address: string;
}
interface ParkingEditModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  parkingId: string;
  initialData: FormData;
  onSuccess?: () => void;
}
export const ParkingEditModal = ({ open, setOpen, parkingId, initialData, onSuccess }: ParkingEditModalProps) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<"geo" | "municipality" | "type" | "payment" | null>(null);
  const [step, setStep] = useState<ModalStep>("initial");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [language, setLanguage] = useState<Language>("EN");

  const [form, setForm] = useState<FormData>({
    parking_name_mk: initialData.parking_name_mk,
    parking_name_en: initialData.parking_name_en,
    parking_name_al: initialData.parking_name_al,
    longitude: initialData.longitude,
    latitude: initialData.latitude,
    municipality_id: initialData.municipality_id,
    area_type: initialData.area_type,
    total_parking_slots: initialData.total_parking_slots,
    payment_options: initialData.payment_options,
    amenities: initialData.amenities,
    address: initialData.address,
  });

  const [updateParking, { isLoading }] = useUpdateParkingMutation();

  const handleChange = (key: keyof FormData, value: string | number | number[]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const openSectionModal = (section: typeof activeSection) => {
    setActiveSection(section);
    setEditModalOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    setStep("initial");
    setErrorMessage(null);
  };

  const handleSubmit = async () => {
    try {
      await updateParking({
        parking_id: parkingId,
        lang: language,
        data: {
          ...form,
          municipality_id: Number(form.municipality_id),
          total_parking_slots: Number(form.total_parking_slots),
          payment_options: form.payment_options.map(Number).filter((n) => !isNaN(n)),
          amenities: form.amenities.map(Number).filter((n) => !isNaN(n)),
        },
      }).unwrap();
      setStep("success");
      if (onSuccess) onSuccess();
    } catch (error: any) {
      setStep("error");
      setErrorMessage(error?.message || "Failed to update parking");
    }
  };

  const getParkingNameByLanguage = () => {
    switch (language) {
      case "MK":
        return form.parking_name_mk;
      case "AL":
        return form.parking_name_al;
      case "EN":
        return form.parking_name_en;
      default:
        return form.parking_name_mk;
    }
  };

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
    // When language changes, update the displayed parking name input
    const parkingNameKey = `parking_name_${newLanguage.toLowerCase()}` as keyof FormData;
    const currentName = form[parkingNameKey] as string;
    setForm((prev) => ({
      ...prev,
      // Keep all other fields the same
    }));
  };

  return (
    <Modal open={open} setOpen={setOpen} withClose={step === "initial"} onlyCloseButton>
      <div className="flex flex-col gap-6 p-2">
        {step === "initial" && (
          <>
            <h3 className="text-xs font-light uppercase text-primary">
              Edit <span className="font-bold text-primary">{getParkingNameByLanguage()}</span>
            </h3>

            {/* Language Selector */}
            <div className="space-y-2">
              <h4 className="font-semibold">{t("parkingManagement.language")}</h4>
              <select
                value={language}
                onChange={(e) => handleLanguageChange(e.target.value as Language)}
                className="w-full border px-3 py-2"
              >
                <option value="MK">Macedonian (MK)</option>
                <option value="EN">English (EN)</option>
                <option value="AL">Albanian (AL)</option>
              </select>
            </div>

            {/* Municipality and Geo */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">{t("parkingManagement.municipalityAndGeolocation")}</h4>
                <button
                  onClick={() => openSectionModal("municipality")}
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <Pencil />
                  {t("common.edit")}
                </button>
              </div>
              {language === "MK" && (
                <input
                  value={form.parking_name_mk}
                  onChange={(e) => handleChange("parking_name_mk", e.target.value)}
                  className="w-full border px-3 py-2"
                  placeholder={t("parkingManagement.parkingNameMk")}
                />
              )}
              {language === "AL" && (
                <input
                  value={form.parking_name_al}
                  onChange={(e) => handleChange("parking_name_al", e.target.value)}
                  className="w-full border px-3 py-2"
                  placeholder={t("parkingManagement.parkingNameAl")}
                />
              )}
              {language === "EN" && (
                <input
                  value={form.parking_name_en}
                  onChange={(e) => handleChange("parking_name_en", e.target.value)}
                  className="w-full border px-3 py-2"
                  placeholder={t("parkingManagement.parkingNameEn")}
                />
              )}
              <input
                type="number"
                value={form.municipality_id}
                onChange={(e) => handleChange("municipality_id", Number(e.target.value))}
                className="w-full border px-3 py-2"
                placeholder={t("parkingManagement.municipalityId")}
              />
              <div className="flex gap-2">
                <input
                  value={form.latitude}
                  onChange={(e) => handleChange("latitude", e.target.value)}
                  className="w-1/2 border px-3 py-2"
                  placeholder={t("parkingManagement.latitude")}
                />
                <input
                  value={form.longitude}
                  onChange={(e) => handleChange("longitude", e.target.value)}
                  className="w-1/2 border px-3 py-2"
                  placeholder={t("parkingManagement.longitude")}
                />
              </div>
            </div>

            {/* Parking Type and Slots */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">{t("parkingManagement.parkingTypeAndSlots")}</h4>
                <button
                  onClick={() => openSectionModal("type")}
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <Pencil />
                  {t("common.edit")}
                </button>
              </div>
              <input
                value={form.area_type}
                onChange={(e) => handleChange("area_type", e.target.value)}
                className="w-full border px-3 py-2"
                placeholder={t("parkingManagement.areaType")}
              />
              <input
                type="number"
                value={form.total_parking_slots}
                onChange={(e) => handleChange("total_parking_slots", Number(e.target.value))}
                className="w-full border px-3 py-2"
                placeholder={t("parkingManagement.totalParkingSlots")}
              />
            </div>

            {/* Payment and Amenities */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">{t("parkingManagement.paymentOptionsAndAmenities")}</h4>
                <button
                  onClick={() => openSectionModal("payment")}
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <Pencil />
                  {t("common.edit")}
                </button>
              </div>
              <input
                value={form.payment_options.join(", ")}
                onChange={(e) =>
                  handleChange(
                    "payment_options",
                    e.target.value
                      .split(",")
                      .map(Number)
                      .filter((n) => !isNaN(n))
                  )
                }
                className="w-full border px-3 py-2"
                placeholder={t("parkingManagement.paymentOptionsCommaSeparated")}
              />
              <input
                value={form.amenities.join(", ")}
                onChange={(e) =>
                  handleChange(
                    "amenities",
                    e.target.value
                      .split(",")
                      .map(Number)
                      .filter((n) => !isNaN(n))
                  )
                }
                className="w-full border px-3 py-2"
                placeholder={t("parkingManagement.amenitiesOptionsCommaSeparated")}
              />
            </div>

            <WithActions closeButton={t("common.close")} setOpen={onClose}>
              <Button onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? t("parkingManagement.saving") : t("common.save")}
              </Button>
            </WithActions>
          </>
        )}

        {step === "success" && (
          <div className="text-center">
            <h3 className="text-lg font-semibold">{t("parkingManagement.parkingUpdated")}</h3>
            <div className="justify-content-center mt-5 flex flex-col items-center gap-2">
              <Button onClick={onClose}>{t("common.close")}</Button>
            </div>
          </div>
        )}

        {step === "error" && (
          <div className="text-center">
            <h3 className="text-lg font-semibold text-red-500">{t("parkingManagement.errorUpdatingParking")}</h3>
            <p>{errorMessage}</p>
            <Button onClick={onClose}>{t("common.close")}</Button>
          </div>
        )}
      </div>

      {activeSection && (
        <ParkingEditSeparateModal
          open={editModalOpen}
          setOpen={setEditModalOpen}
          section={activeSection}
          formData={form}
          updateForm={handleChange}
        />
      )}
    </Modal>
  );
};
